import { useCallback, useEffect, useMemo, useState } from 'react';

const parseHour = (value, fallback = 0) => {
  if (value === '' || value === null || value === undefined) {
    return fallback;
  }
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  return Math.max(0, Math.min(23, parsed));
};

export const formatDaypartHour = (value) => `${String(parseHour(value)).padStart(2, '0')}:00`;

export const formatDaypartRange = (daypart) => {
  const start = parseHour(daypart?.hora_inicio);
  const end = parseHour(daypart?.hora_fin);
  const suffix = end <= start ? ' (+1 día)' : '';
  return `${formatDaypartHour(start)} - ${formatDaypartHour(end)}${suffix}`;
};

const segmentsForRange = (start, end) => (
  start < end ? [[start, end]] : [[start, 24], [0, end]]
);

const segmentsOverlap = (left, right) => Math.max(left[0], right[0]) < Math.min(left[1], right[1]);

export const findDaypartConflict = (candidate, dayparts = []) => {
  const start = parseHour(candidate?.hora_inicio);
  const end = parseHour(candidate?.hora_fin);
  if (start === end) {
    return 'La hora de inicio y fin no pueden ser iguales.';
  }
  if (!candidate?.activo) {
    return null;
  }
  const candidateName = (candidate?.nombre || '').trim().toLowerCase();
  const candidateSegments = segmentsForRange(start, end);
  const candidateId = candidate?.id ? Number(candidate.id) : null;

  for (const item of dayparts) {
    if (!item?.activo) {
      continue;
    }
    if (candidateId && Number(item.id) === candidateId) {
      continue;
    }
    const itemName = (item.nombre || '').trim().toLowerCase();
    if (candidateName && itemName === candidateName) {
      return `Ya existe un daypart activo con el nombre "${item.nombre}".`;
    }
    const itemSegments = segmentsForRange(parseHour(item.hora_inicio), parseHour(item.hora_fin));
    const hasOverlap = candidateSegments.some((candidateSegment) => (
      itemSegments.some((itemSegment) => segmentsOverlap(candidateSegment, itemSegment))
    ));
    if (hasOverlap) {
      return `El rango se solapa con "${item.nombre}".`;
    }
  }
  return null;
};

const normalizePayload = (values = {}) => ({
  nombre: (values.nombre || '').trim(),
  hora_inicio: parseHour(values.hora_inicio),
  hora_fin: parseHour(values.hora_fin),
  activo: Boolean(values.activo),
  sort_order: Number.isFinite(Number(values.sort_order)) ? Number.parseInt(values.sort_order, 10) : 0
});

export const useAdminDayparts = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const fetchList = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/admin/api/dayparts', { signal });
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || 'No se pudieron obtener los dayparts.');
      }
      if (!signal?.aborted) {
        setItems(data.dayparts || []);
      }
    } catch (err) {
      if (!signal?.aborted) {
        setError(err?.message || 'Error al cargar dayparts.');
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchList(controller.signal);
    return () => controller.abort();
  }, [fetchList, refreshToken]);

  const refresh = useCallback(() => {
    setRefreshToken((value) => value + 1);
  }, []);

  const saveDaypart = useCallback(async (values) => {
    const payload = normalizePayload(values);
    if (!payload.nombre) {
      throw new Error('El nombre del daypart es requerido.');
    }
    if (payload.hora_inicio === payload.hora_fin) {
      throw new Error('La hora de inicio y fin no pueden ser iguales.');
    }
    const hasId = Boolean(values?.id);
    const url = hasId ? `/admin/api/dayparts/${values.id}` : '/admin/api/dayparts';
    const method = hasId ? 'PUT' : 'POST';
    const response = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo guardar el daypart.');
    }
    return data;
  }, []);

  const deleteDaypart = useCallback(async (id) => {
    const response = await fetch(`/admin/api/dayparts/${id}`, { method: 'DELETE' });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo eliminar el daypart.');
    }
    return data;
  }, []);

  return useMemo(() => ({
    items,
    loading,
    error,
    refresh,
    saveDaypart,
    deleteDaypart
  }), [items, loading, error, refresh, saveDaypart, deleteDaypart]);
};
